import React, { useState } from "react";
import { Menu, Trash2, MoreVertical, Edit2, EyeOff, Eye } from "lucide-react";
import api from "../services/apiService";

const MenuItemsDisplay = ({
  filteredItems,
  viewMode,
  selectedCategory,
  getCategoryColor,
  getCategoryName,
  handleDeleteMenuItem,
  handleEditMenuItem,
  restaurantId,
  setMenuItems,
}) => {
  const [menuOpenId, setMenuOpenId] = useState(null);

  // Handler to toggle availability
  const handleToggleAvailability = async (item) => {
    if (!item || !item.id) return;
    try {
      const token = localStorage.getItem("token");
      // If currently available, mark unavailable (send false). If unavailable, mark available (send true)
      const newAvailability = item.available === true ? false : true;
      await api.put(
        `menu/availability/${item.id}?isAvailable=${newAvailability}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Fetch updated menu items if setMenuItems and restaurantId are provided
      if (setMenuItems && restaurantId) {
        const menuRes = await api.get(`menu/restaurant/${restaurantId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMenuItems(menuRes.data);
      }
    } catch (err) {
      alert("Failed to update availability");
    }
  };
  // Default image SVG for grid and list
  const DefaultImageSVG = ({ grid }) =>
    grid ? (
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 400 192"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-48 object-cover bg-gray-100"
      >
        <rect width="400" height="192" rx="24" fill="#F3F4F6" />
        <circle cx="200" cy="96" r="56" fill="#E5E7EB" />
        <ellipse cx="200" cy="140" rx="80" ry="24" fill="#E5E7EB" />
        <rect x="120" y="60" width="160" height="32" rx="16" fill="#D1D5DB" />
        <rect x="160" y="104" width="80" height="16" rx="8" fill="#D1D5DB" />
        <text
          x="200"
          y="100"
          textAnchor="middle"
          fill="#9CA3AF"
          fontSize="20"
          fontWeight="bold"
        >
          No Image
        </text>
      </svg>
    ) : (
      <span className="w-16 h-16 block rounded-lg overflow-hidden bg-gray-100">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-16 h-16 object-cover"
        >
          <rect width="64" height="64" rx="12" fill="#F3F4F6" />
          <circle cx="32" cy="32" r="18" fill="#E5E7EB" />
          <ellipse cx="32" cy="48" rx="24" ry="7" fill="#E5E7EB" />
          <rect x="20" y="20" width="24" height="8" rx="4" fill="#D1D5DB" />
          <rect x="26" y="36" width="12" height="4" rx="2" fill="#D1D5DB" />
          <text
            x="32"
            y="36"
            textAnchor="middle"
            fill="#9CA3AF"
            fontSize="8"
            fontWeight="bold"
          >
            No Img
          </text>
        </svg>
      </span>
    );

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900">
            {selectedCategory === "all"
              ? "All Menu Items"
              : getCategoryName(selectedCategory)}
            <span className="ml-2 text-base text-gray-500 font-normal">
              ({filteredItems.length} items)
            </span>
          </h3>
          {selectedCategory !== "all" && (
            <div
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: getCategoryColor(selectedCategory) }}
            />
          )}
        </div>
      </div>
      <div className="p-6">
        {filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Menu className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No menu items found</p>
            <p className="text-gray-400 text-sm mt-1">
              {/* ...existing code... */}
              Try adjusting your search terms or add your first menu item to get
              started
            </p>
          </div>
        ) : (
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                : "space-y-4"
            }
          >
            {filteredItems.map((item, index) =>
              viewMode === "grid" ? (
                <div
                  key={item.id}
                  className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 animate-in slide-in-from-bottom-4 fill-mode-both relative"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* Coming Soon overlay if isAvailable is explicitly false */}
                  <div className="relative">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-48 object-cover"
                      />
                    ) : (
                      <DefaultImageSVG grid />
                    )}
                    {item.available === false && (
                      <div className="absolute inset-0 h-48 bg-black bg-opacity-60 flex items-center justify-center z-20">
                        <span className="text-white text-lg font-bold">
                          Coming Soon...
                        </span>
                      </div>
                    )}
                    <div
                      className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium text-white shadow-sm"
                      style={{
                        backgroundColor: getCategoryColor(item.category),
                      }}
                    >
                      {getCategoryName(item.category)}
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900 text-lg leading-tight">
                          {item.name}
                        </h4>
                        {item.quantity && (
                          <span className="text-xs text-gray-500 font-medium block mt-1">
                            {item.quantity}
                          </span>
                        )}
                      </div>
                      <div className="relative">
                        <button
                          onClick={() =>
                            setMenuOpenId(
                              menuOpenId === item.id ? null : item.id
                            )
                          }
                          className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {menuOpenId === item.id && (
                          <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                            <button
                              onClick={() => {
                                setMenuOpenId(null);
                                handleEditMenuItem(item);
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                setMenuOpenId(null);
                                handleToggleAvailability(item);
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              {item.available === true ? (
                                <>
                                  <EyeOff className="w-4 h-4 mr-2" />
                                  Unavailable
                                </>
                              ) : (
                                <>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Available
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setMenuOpenId(null);
                                if (
                                  window.confirm(
                                    "Are you sure you want to delete this menu item?"
                                  )
                                ) {
                                  handleDeleteMenuItem(item.id);
                                }
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {item.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {item.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-emerald-600">
                        ${parseFloat(item.price).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-all animate-in slide-in-from-left-4 fill-mode-both relative"
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  {/* Coming Soon overlay if isAvailable is explicitly false */}
                  {item.available === false && (
                    <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center z-20">
                      <span className="text-white text-lg font-bold px-4 py-2 rounded-lg bg-emerald-600 bg-opacity-80">
                        Currently Unavailable
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-4 flex-1">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    ) : (
                      <DefaultImageSVG grid={false} />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {item.name}
                          </h4>
                          {item.quantity && (
                            <span className="text-xs text-gray-500 font-medium block mt-1">
                              {item.quantity}
                            </span>
                          )}
                        </div>
                        <span
                          className="px-2 py-1 rounded-full text-xs font-medium text-white"
                          style={{
                            backgroundColor: getCategoryColor(item.category),
                          }}
                        >
                          {getCategoryName(item.category)}
                        </span>
                      </div>
                      {item.description && (
                        <p className="text-sm text-gray-600 line-clamp-1">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-lg font-bold text-emerald-600">
                      ${parseFloat(item.price).toFixed(2)}
                    </span>
                    <div className="relative">
                      <button
                        onClick={() =>
                          setMenuOpenId(menuOpenId === item.id ? null : item.id)
                        }
                        className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {menuOpenId === item.id && (
                        <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                          <button
                            onClick={() => {
                              setMenuOpenId(null);
                              handleEditMenuItem(item);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setMenuOpenId(null);
                              handleToggleAvailability(item);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {item.available === true ? (
                              <EyeOff className="w-4 h-4 mr-2" />
                            ) : (
                              <Eye className="w-4 h-4 mr-2" />
                            )}
                            {item.isAvailable === true
                              ? "Mark Unavailable"
                              : "Mark Available"}
                          </button>
                          <button
                            onClick={() => {
                              setMenuOpenId(null);
                              if (
                                window.confirm(
                                  "Are you sure you want to delete this menu item?"
                                )
                              ) {
                                handleDeleteMenuItem(item.id);
                              }
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuItemsDisplay;
